import { assertNever } from '@gitbook/util-ts';

import type {
    APIChannelMutationData,
    SubscriptionChannel,
    SubscriptionChannelData,
} from './__generated__';

/**
 * Get the type of data associated to a channel.
 */
export type DataForSubscriptionChannel<C extends SubscriptionChannel> =
    DefinedDataForSubscriptionChannel<C> extends never
        ? APIChannelMutationData['data']
        : DefinedDataForSubscriptionChannel<C>;

type DefinedDataForSubscriptionChannel<C extends SubscriptionChannel> = C extends {
    channel: infer ChannelName;
}
    ? Extract<SubscriptionChannelData, { channel: ChannelName }>['data']
    : never;

/**
 * Get a channel ID from a subscription channel.
 */
export function getSubscriptionChannelId(channel: SubscriptionChannel): string {
    let channelId = '';

    switch (channel.channel) {
        case 'space':
            channelId = `spaces:${channel.space}`;
            break;
        case 'space-content':
            channelId = `spaces:${channel.space}:content`;
            break;
        case 'space-git-info':
            channelId = `spaces:${channel.space}:git`;
            break;
        case 'space-publishing-auth':
            channelId = `spaces:${channel.space}:publishing:auth`;
            break;
        case 'space-publishing-customization':
            channelId = `spaces:${channel.space}:publishing:customization`;
            break;
        case 'collection':
            channelId = `collections:${channel.collection}`;
            break;
        case 'collection-publishing-customization':
            channelId = `collections:${channel.collection}:publishing:customization`;
            break;
        case 'space-change-requests':
            channelId = `spaces:${channel.space}:change-requests`;
            break;
        case 'space-change-request':
            channelId = `spaces:${channel.space}:change-requests:${channel.changeRequest}`;
            break;
        case 'space-change-request-reviews':
            channelId = `spaces:${channel.space}:change-requests:${channel.changeRequest}:reviews`;
            break;
        case 'space-change-request-content-refs':
            channelId = `spaces:${channel.space}:change-requests:${channel.changeRequest}:content-refs`;
            break;
        case 'space-content-refs':
            channelId = `spaces:${channel.space}:content-refs`;
            break;
        case 'space-integrations':
            channelId = `spaces:${channel.space}:integrations`;
            break;
        case 'space-comments':
            channelId = `spaces:${channel.space}:comments`;
            break;
        case 'space-change-request-comments':
            channelId = `spaces:${channel.space}:change-requests:${channel.changeRequest}:comments`;
            break;
        case 'space-comment':
            channelId = `spaces:${channel.space}:comments:${channel.comment}`;
            break;
        case 'space-comment-replies':
            channelId = `spaces:${channel.space}:comments:${channel.comment}:replies`;
            break;
        case 'organization':
            channelId = `organizations:${channel.organization}`;
            break;
        case 'organization-link-invites':
            channelId = `organizations:${channel.organization}:link-invites`;
            break;
        case 'organization-members':
            channelId = `organizations:${channel.organization}:members`;
            break;
        case 'organization-member':
            channelId = `organizations:${channel.organization}:members:${channel.user}`;
            break;
        case 'organization-teams':
            channelId = `organizations:${channel.organization}:teams`;
            break;
        case 'organization-saml':
            channelId = `organizations:${channel.organization}:saml`;
            break;
        case 'organization-team':
            channelId = `organizations:${channel.organization}:team:${channel.team}`;
            break;
        case 'organization-team-members':
            channelId = `organizations:${channel.organization}:team:${channel.team}:members`;
            break;
        case 'organization-team-member':
            channelId = `organizations:${channel.organization}:team:${channel.team}:member:${channel.member}`;
            break;
        case 'organization-spaces':
            channelId = `organizations:${channel.organization}:spaces`;
            break;
        case 'organization-change-requests':
            channelId = `organizations:${channel.organization}:change-requests`;
            break;
        case 'organization-collections':
            channelId = `organizations:${channel.organization}:collections`;
            break;
        case 'organization-integrations':
            channelId = `organizations:${channel.organization}:integrations`;
            break;
        case 'organization-installations':
            channelId = `organizations:${channel.organization}:installations`;
            break;
        case 'organization-ai-indexation':
            channelId = `organizations:${channel.organization}:ai-indexation`;
            break;
        case 'organization-agent-instructions':
            channelId = `organizations:${channel.organization}:agent-instructions`;
            break;
        case 'user-api-tokens':
            channelId = `users:${channel.user}:api-tokens`;
            break;
        case 'user-organizations':
            channelId = `users:${channel.user}:organizations`;
            break;
        case 'user-profile':
            channelId = `users:${channel.user}:profile`;
            break;
        case 'organization-sites':
            channelId = `organizations:${channel.organization}:sites`;
            break;
        case 'integration':
            channelId = `integrations:${channel.integration}`;
            break;
        case 'integration-installation':
            channelId = `integrations:${channel.integration}:installations:${channel.installation}`;
            break;
        case 'integration-space-installations':
            channelId = `integrations:${channel.integration}:installations:${channel.installation}:spaces`;
            break;
        case 'integration-space-installation':
            channelId = `integrations:${channel.integration}:installations:${channel.installation}:spaces:${channel.space}`;
            break;
        case 'integration-site-installation':
            channelId = `integrations:${channel.integration}:installations:${channel.installation}:sites:${channel.site}`;
            break;
        case 'integration-site-installations':
            channelId = `integrations:${channel.integration}:installations:${channel.installation}:sites`;
            break;
        case 'published-content-site':
            channelId = `sites:${channel.site}:published`;
            break;
        case 'site':
            channelId = `sites:${channel.site}`;
            break;
        case 'site-structure':
            channelId = `sites:${channel.site}:structure`;
            break;
        case 'site-space':
            channelId = `sites:${channel.site}:spaces:${channel.siteSpace}`;
            break;
        case 'site-customization':
            channelId = `sites:${channel.site}:customization`;
            break;
        case 'site-integrations':
            channelId = `sites:${channel.site}:integrations`;
            break;
        case 'site-space-customization':
            channelId = `sites:${channel.site}:spaces:${channel.siteSpace}:customization`;
            break;
        case 'site-publishing-auth':
            channelId = `sites:${channel.site}:publishing-auth`;
            break;
        case 'site-share-links':
            channelId = `sites:${channel.site}:share-links`;
            break;
        case 'site-share-link':
            channelId = `sites:${channel.site}:share-links:${channel.siteShareLink}`;
            break;
        case 'site-mcp-servers':
            channelId = `sites:${channel.site}:mcp-servers`;
            break;
        case 'site-mcp-server':
            channelId = `sites:${channel.site}:mcp-servers:${channel.siteMcpServer}`;
            break;
        case 'site-section':
            channelId = `sites:${channel.site}:sections:${channel.siteSection}`;
            break;
        case 'site-redirects':
            channelId = `sites:${channel.site}:redirects`;
            break;
        case 'site-adaptive-schema':
            channelId = `sites:${channel.site}:site-adaptive-schema`;
            break;
        case 'custom-hostname':
            channelId = `custom-hostname:${channel.customHostname}`;
            break;
        case 'space-gitsync-operation':
            channelId = `spaces:${channel.space}:gitsync:operation`;
            break;
        case 'organization-openapi-specs':
            channelId = `organizations:${channel.organization}:openapi-specs`;
            break;
        case 'openapi-spec':
            channelId = `openapi-specs:${channel.openAPISpec}`;
            break;
        case 'organization-fonts':
            channelId = `organizations:${channel.organization}:custom-fonts`;
            break;
        case 'subdomain':
            channelId = `subdomain:${channel.subdomain}`;
            break;
        case 'organization-translations':
            channelId = `organizations:${channel.organization}:translations`;
            break;
        case 'organization-translations-glossary':
            channelId = `organizations:${channel.organization}:translations-glossary`;
            break;
        case 'translation':
            channelId = `translations:${channel.translation}`;
            break;
        case 'translation-run':
            channelId = `translations:${channel.translation}:run`;
            break;
        case 'import-run':
            channelId = `imports:${channel.runId}`;
            break;
        default:
            assertNever(channel);
    }

    return channelId;
}
